import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Building2, AlertCircle, ArrowLeft, ArrowRight, Check } from 'lucide-react';

const CompanySignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Capture ?ref=<code> on mount and persist for forwarding in submit.
  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) {
      localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
    }
  }, [location.search]);

  const [step, setStep] = useState(1); // 1: company info, 2: address, 3: bank & password
  const [formData, setFormData] = useState({
    // Company details
    companyName: '',
    companyEmail: '',
    companyRegistrationNumber: '',
    vatNumber: '',
    directorName: '',
    companyStartDate: '',
    // Address
    registeredOfficeAddress: '',
    registeredCity: '',
    registeredPostcode: '',
    // Bank & Auth
    bankAccountName: '',
    bankAccountNumber: '',
    bankSortCode: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.companyName) newErrors.companyName = 'Company name is required';
      if (!formData.companyEmail) newErrors.companyEmail = 'Company email is required';
      if (!formData.companyRegistrationNumber) newErrors.companyRegistrationNumber = 'Registration number is required';
      if (!formData.directorName) newErrors.directorName = 'Director name is required';
      if (!formData.companyStartDate) newErrors.companyStartDate = 'Company start date is required';
    }
    
    if (currentStep === 2) {
      if (!formData.registeredOfficeAddress) newErrors.registeredOfficeAddress = 'Address is required';
      if (!formData.registeredCity) newErrors.registeredCity = 'City is required';
      if (!formData.registeredPostcode) newErrors.registeredPostcode = 'Postcode is required';
    }
    
    if (currentStep === 3) {
      if (!formData.bankAccountName) newErrors.bankAccountName = 'Account name is required';
      if (!formData.bankAccountNumber) newErrors.bankAccountNumber = 'Account number is required';
      if (!formData.bankSortCode) newErrors.bankSortCode = 'Sort code is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      if (formData.bankAccountNumber && !/^\d{8}$/.test(formData.bankAccountNumber)) {
        newErrors.bankAccountNumber = 'Account number must be 8 digits';
      }
      if (formData.bankSortCode && !/^\d{6}$/.test(formData.bankSortCode.replace(/-/g, ''))) {
        newErrors.bankSortCode = 'Sort code must be 6 digits';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    
    setLoading(true);
    setError('');

    try {
      const result = await userAPI.companyHostSignup({
        companyEmail: formData.companyEmail,
        password: formData.password,
        companyName: formData.companyName,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        registeredOfficeAddress: formData.registeredOfficeAddress,
        registeredCity: formData.registeredCity,
        registeredPostcode: formData.registeredPostcode,
        vatNumber: formData.vatNumber || null,
        directorName: formData.directorName,
        companyStartDate: formData.companyStartDate,
        bankAccountName: formData.bankAccountName,
        bankAccountNumber: formData.bankAccountNumber,
        bankSortCode: formData.bankSortCode.replace(/-/g, ''),
        referredBy: localStorage.getItem('bc_ref_code') || undefined
      });

      // The backend set HttpOnly access + refresh cookies on this response,
      // so the company host is already authenticated. We no longer stash any
      // token or user blob in web storage (XSS-readable). A full navigation
      // re-bootstraps AuthContext from the refresh cookie cleanly.
      toast({
        title: 'Company Account Created!',
        description: result.message || `You're signed in as ${formData.companyName}.`
      });

      // Full reload so AuthContext re-runs its cookie-based bootstrap and
      // picks up the new company session.
      window.location.href = '/host/dashboard';
    } catch (error) {
      console.error('Company signup error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create company account';
      setError(errorMessage);
      toast({
        title: 'Signup Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3].map((s, index) => (
        <React.Fragment key={s}>
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
              s < step 
                ? 'bg-green-500 text-white' 
                : s === step 
                  ? 'bg-[#1F3A68] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {s < step ? <Check size={16} /> : s}
          </div>
          {index < 2 && (
            <div className={`w-12 h-1 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Company Details</h3>
        <p className="text-sm text-gray-600">Enter your registered company information</p>
      </div>
      
      <div>
        <Label>Company Name *</Label>
        <Input
          placeholder="Acme Properties Ltd"
          value={formData.companyName}
          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
          className={errors.companyName ? 'border-red-500' : ''}
        />
        {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
      </div>
      
      <div>
        <Label>Company Email *</Label>
        <Input
          type="email"
          placeholder="contact@company.com"
          value={formData.companyEmail}
          onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
          className={errors.companyEmail ? 'border-red-500' : ''}
        />
        {errors.companyEmail && <p className="text-red-500 text-xs mt-1">{errors.companyEmail}</p>}
        <p className="text-xs text-gray-500 mt-1">This will be your login email</p>
      </div>
      
      <div>
        <Label>Company Registration Number *</Label>
        <Input
          placeholder="12345678"
          value={formData.companyRegistrationNumber}
          onChange={(e) => setFormData({ ...formData, companyRegistrationNumber: e.target.value })}
          className={errors.companyRegistrationNumber ? 'border-red-500' : ''}
        />
        {errors.companyRegistrationNumber && <p className="text-red-500 text-xs mt-1">{errors.companyRegistrationNumber}</p>}
      </div>
      
      <div>
        <Label>VAT Number (Optional)</Label>
        <Input
          placeholder="GB123456789"
          value={formData.vatNumber}
          onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
        />
      </div>
      
      <div>
        <Label>Director / Authorised Person *</Label>
        <Input
          placeholder="John Smith"
          value={formData.directorName}
          onChange={(e) => setFormData({ ...formData, directorName: e.target.value })}
          className={errors.directorName ? 'border-red-500' : ''}
        />
        {errors.directorName && <p className="text-red-500 text-xs mt-1">{errors.directorName}</p>}
      </div>
      
      <div>
        <Label>Company Start Date *</Label>
        <Input
          type="date"
          value={formData.companyStartDate}
          onChange={(e) => setFormData({ ...formData, companyStartDate: e.target.value })}
          className={errors.companyStartDate ? 'border-red-500' : ''}
        />
        {errors.companyStartDate && <p className="text-red-500 text-xs mt-1">{errors.companyStartDate}</p>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Registered Address</h3>
        <p className="text-sm text-gray-600">Enter your company's registered office address</p>
      </div>
      
      <div>
        <Label>Registered Office Address *</Label>
        <Input
          placeholder="123 Business Street"
          value={formData.registeredOfficeAddress}
          onChange={(e) => setFormData({ ...formData, registeredOfficeAddress: e.target.value })}
          className={errors.registeredOfficeAddress ? 'border-red-500' : ''}
        />
        {errors.registeredOfficeAddress && <p className="text-red-500 text-xs mt-1">{errors.registeredOfficeAddress}</p>}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>City *</Label>
          <Input
            placeholder="London"
            value={formData.registeredCity}
            onChange={(e) => setFormData({ ...formData, registeredCity: e.target.value })}
            className={errors.registeredCity ? 'border-red-500' : ''}
          />
          {errors.registeredCity && <p className="text-red-500 text-xs mt-1">{errors.registeredCity}</p>}
        </div>
        <div>
          <Label>Postcode *</Label>
          <Input
            placeholder="SW1A 1AA"
            value={formData.registeredPostcode}
            onChange={(e) => setFormData({ ...formData, registeredPostcode: e.target.value })}
            className={errors.registeredPostcode ? 'border-red-500' : ''}
          />
          {errors.registeredPostcode && <p className="text-red-500 text-xs mt-1">{errors.registeredPostcode}</p>}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Bank & Login Details</h3>
        <p className="text-sm text-gray-600">Set up your payout account and login credentials</p>
      </div>
      
      <div>
        <Label>Bank Account Name *</Label>
        <Input
          placeholder="Company name on bank account"
          value={formData.bankAccountName}
          onChange={(e) => setFormData({ ...formData, bankAccountName: e.target.value })}
          className={errors.bankAccountName ? 'border-red-500' : ''}
        />
        {errors.bankAccountName && <p className="text-red-500 text-xs mt-1">{errors.bankAccountName}</p>}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Account Number *</Label>
          <Input
            placeholder="12345678"
            value={formData.bankAccountNumber}
            onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            className={errors.bankAccountNumber ? 'border-red-500' : ''}
          />
          {errors.bankAccountNumber && <p className="text-red-500 text-xs mt-1">{errors.bankAccountNumber}</p>}
        </div>
        <div>
          <Label>Sort Code *</Label>
          <Input
            placeholder="12-34-56"
            value={formData.bankSortCode}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '').slice(0, 6);
              if (value.length > 2) value = value.slice(0, 2) + '-' + value.slice(2);
              if (value.length > 5) value = value.slice(0, 5) + '-' + value.slice(5);
              setFormData({ ...formData, bankSortCode: value });
            }}
            className={errors.bankSortCode ? 'border-red-500' : ''}
          />
          {errors.bankSortCode && <p className="text-red-500 text-xs mt-1">{errors.bankSortCode}</p>}
        </div>
      </div>
      
      <div className="pt-4 border-t">
        <p className="text-sm text-gray-600 mb-4">Create your login password</p>
        
        <div>
          <Label>Password *</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={errors.password ? 'border-red-500' : ''}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
        </div>
        
        <div className="mt-4">
          <Label>Confirm Password *</Label>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            className={errors.confirmPassword ? 'border-red-500' : ''}
          />
          {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
        </div>

        <div className="flex items-start gap-2 mt-4">
          <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
          <span className="text-xs text-gray-600">
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms & Conditions</a>
            {' '}and{' '}
            <a href="/terms#data-protection" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>,
            including the Host Terms (Section 5) covering auto-dispatch, Smart Lock, and Right-to-Rent obligations.
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/">
            <div className="h-[42px] w-auto flex items-center justify-center flex-shrink-0 mx-auto mb-3"><img src="/logo.png" alt="Ariv" className="h-[42px] w-auto object-contain" /></div>
          </Link>
          <div className="flex items-center justify-center gap-2">
            <Building2 className="text-[#1F3A68]" size={24} />
            <h1 className="text-2xl font-bold text-[#1F3A68]">Company Host Signup</h1>
          </div>
          <p className="text-gray-600 mt-1">Register your company as a host</p>
        </div>

        <Card className="border-2 border-[#1F3A68]/10 shadow-xl">
          <CardContent className="pt-6">
            {renderStepIndicator()}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              
              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1"
                  >
                    <ArrowLeft size={16} className="mr-1" />
                    Back
                  </Button>
                )}
                
                {step < 3 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="flex-1 bg-[#1F3A68]"
                  >
                    Next
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="flex-1 bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Company Account'}
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Want to sign up as an individual?{' '}
                <Link to="/signup" className="text-[#1F3A68] font-semibold hover:underline">
                  Go back
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-xs text-gray-500 mt-4">
          Company accounts are separate login identities. You cannot switch between personal and company accounts without logging out.
        </p>
      </div>
    </div>
  );
};

export default CompanySignupPage;
